import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { PlatformModule } from '@angular/cdk/platform';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { NgModule, APP_INITIALIZER, ErrorHandler, APP_ID } from '@angular/core';
import { RouterModule } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import { MAT_LEGACY_SELECT_CONFIG as MAT_SELECT_CONFIG } from '@angular/material/legacy-select';

import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';

import { SAJScriptService } from '@snagajob/angular/services/script';
import { APP_CONFIG } from '@libs/configs';
import { environment } from '../environments/environment';

// if using auth, authentication must be imported at the top level so
// it's guards and services can be provided for. see README within authentication module
// for usage within an app and applying guards to routes
import {
  SnagAuthenticationModule,
  ClaimGuard,
  DefaultPageGuard,
  SelfSignupSettingGuard,
  AuthenticationTokenInterceptor,
} from '@libs/authentication';
import { AuthenticationService } from '@libs/authentication/services';

// layout module to manage desktop vs mobile layouts
import { SnagLayoutModule } from '@libs/layout';

// Importing the filter at the app level to ensure importing isn't duplicated
import { SAJMultiFilterModule } from '@libs/multi-filter';

import { gotoChallenge, LocalStorageService } from '@libs/services';
import { AccountsApiService } from '@libs/customers/accounts';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { TRACKING_SETTINGS } from '@snagajob/adobe';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideCloudflareChallengeInterceptor } from '@snagajob/ngx-cf-challenge-interceptor';
import { LegacyEcommAccountGuard } from '@libs/authentication/guards/legacy-ecomm-account.guard';
import { firstValueFrom, of } from 'rxjs';
import { BrowserCookiesModule } from '@seeplusplus/ngx-utils-cookies-port';
import { sentryErrorHandler } from './utils/sentry-error-handler';
import { provideNgxMask } from 'ngx-mask';

export function validate(authService: AuthenticationService) {
  return () => firstValueFrom(authService.restoreFromCache());
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    HttpClientModule,
    PlatformModule,
    A11yModule,
    BrowserCookiesModule.forRoot(),
    CarouselModule,

    // snag modules
    SnagAuthenticationModule,
    SnagLayoutModule,
    SAJMultiFilterModule,

    // needed mat imports at top level,
    // see: angular/components/issues/4278
    MatSliderModule,
    MatInputModule,

    RouterModule.forRoot(AppRoutes, {
      initialNavigation: 'enabledNonBlocking',
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.enablePWA,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    AccountsApiService,
    AuthenticationService,
    SAJScriptService,
    LocalStorageService,
    HttpClient,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationTokenInterceptor,
      multi: true,
    },
    {
      provide: TRACKING_SETTINGS,
      useValue: { isTrackingEnabled$: () => of(true) },
    },
    provideCloudflareChallengeInterceptor(gotoChallenge),
    ClaimGuard,
    LegacyEcommAccountGuard,
    DefaultPageGuard,
    SelfSignupSettingGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: validate,
      deps: [AuthenticationService],
      multi: true,
    },
    {
      provide: APP_CONFIG,
      useValue: Object.assign(environment, { app: 'EMPLOYER' }),
    },
    {
      provide: ErrorHandler,
      useValue: sentryErrorHandler,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: MAT_SELECT_CONFIG, useValue: { disableOptionCentering: true } },
    { provide: APP_ID, useValue: 'marketplace-employer' },
    provideNgxMask(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
